/**
 * Applies Wholesale Now discounted prices in the cart drawer and routes
 * wholesale checkout through the Wholesale Now checkout URL API.
 */
(function () {
  'use strict';

  const SETTINGS_URL = 'https://wholesale-pricing-now.herokuapp.com/get_settings';
  const CHECKOUT_URL = 'https://wholesale-pricing-now.herokuapp.com/get_checkout_url';
  let cachedGroups = null;
  let refreshTimer = null;

  function getShopDomain() {
    return window.wn?.datastore?.shop?.shopify_domain
      || window.Shopify?.shop
      || '';
  }

  function getDrawer() {
    return document.querySelector('.js-cart-drawer');
  }

  function getCustomerTags() {
    const tags = window.wn?.customer_tags || getDrawer()?.dataset.customerTags || '';
    return tags.toLowerCase().split(',').map((tag) => tag.trim()).filter(Boolean);
  }

  function isWholesaleCustomer() {
    return getCustomerTags().length > 0;
  }

  function formatMoney(cents, format) {
    const moneyFormat = format || window.wn?.datastore?.shop?.money_format || '${{amount}}';

    if (window.theme?.Shopify?.formatMoney) {
      return window.theme.Shopify.formatMoney(cents, moneyFormat);
    }

    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, moneyFormat);
    }

    return `$${(cents / 100).toFixed(2)}`;
  }

  async function getDiscountGroups() {
    if (cachedGroups) return cachedGroups;

    const embedded = window.wn?.datastore?.discount_groups;
    if (Array.isArray(embedded) && embedded.length > 1) {
      cachedGroups = embedded;
      return cachedGroups;
    }

    const shop = getShopDomain();
    if (!shop) return embedded || [];

    try {
      const response = await fetch(`${SETTINGS_URL}?shopify_domain=${encodeURIComponent(shop)}`);
      if (!response.ok) throw new Error(`Settings request failed: ${response.status}`);
      const data = await response.json();
      cachedGroups = data.discount_groups || embedded || [];
    } catch (error) {
      console.warn('Wholesale cart drawer: unable to load discount groups', error);
      cachedGroups = embedded || [];
    }

    return cachedGroups;
  }

  async function fetchCart() {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`);
    if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
    return response.json();
  }

  function getApplicableGroups(groups, customerTags) {
    return groups.filter((group) => {
      if (!group.discount_enabled) return false;
      const groupTag = (group.customer_tag || '').toLowerCase().trim();
      return customerTags.includes(groupTag);
    });
  }

  function getBestTier(group, cartTotalCents) {
    const tiers = (group.discount_tiers || [])
      .slice()
      .sort((a, b) => (b.min_cart_value || 0) - (a.min_cart_value || 0));

    return tiers.find((tier) => cartTotalCents >= (tier.min_cart_value || 0)) || null;
  }

  function calculateBestDiscount(cart, groups, customerTags) {
    const applicableGroups = getApplicableGroups(groups, customerTags);
    if (!applicableGroups.length) return null;

    let best = null;

    applicableGroups.forEach((group) => {
      const tier = getBestTier(group, cart.original_total_price);
      if (!tier) return;

      const percent = tier.discount_percent || 0;
      if (percent <= 0) return;

      const totalDiscount = Math.floor(cart.original_total_price * percent);
      const totalPrice = cart.original_total_price - totalDiscount;

      if (!best || totalDiscount > best.totalDiscount) {
        best = {
          group,
          tier,
          percent,
          totalDiscount,
          originalTotal: cart.original_total_price,
          totalPrice,
        };
      }
    });

    return best;
  }

  function buildPercentageAppliedDiscount(item, percent) {
    const value = parseFloat((100 * percent).toFixed(2));
    const discountAmount = Math.floor(Math.floor(item.price * value) / 100);
    const totalDiscountAmount = Math.floor(Math.floor(item.price * value * item.quantity) / 100);

    return {
      discount_amount: discountAmount,
      original_line_price: item.original_line_price,
      total_discount_amount: totalDiscountAmount,
      value_type: 'percentage',
      description: 'Percentage Discount',
      title: 'Percentage Discount',
      value,
      amount: totalDiscountAmount / 100,
    };
  }

  function buildLineItemHash(item, appliedDiscount) {
    const properties = [];

    if (item.properties) {
      Object.keys(item.properties).forEach((name) => {
        const value = item.properties[name];
        if (value != null && value !== '') {
          properties.push({ name, value });
        }
      });
    }

    return {
      title: item.title,
      variant_id: item.variant_id,
      product_id: item.product_id,
      quantity: item.quantity,
      price: item.price / 100,
      requires_shipping: item.requires_shipping,
      grams: item.grams,
      properties,
      applied_discount: appliedDiscount,
    };
  }

  function buildDraftOrderPayload(cart, discount) {
    const lineItems = cart.items.map((item) => (
      buildLineItemHash(item, buildPercentageAppliedDiscount(item, discount.percent))
    ));

    const noteEl = getDrawer()?.querySelector('textarea[name="note"]');

    return {
      line_items: JSON.stringify(lineItems),
      customer_tags: window.wn?.customer_tags || getDrawer()?.dataset.customerTags || '',
      customer_id: String(window.wn?.customer_id || getDrawer()?.dataset.customerId || ''),
      order_notes: noteEl ? noteEl.value : '',
      tax_exempt: discount.group.tax_exempt !== false,
      applied_discount_tags_string: discount.group.customer_tag || '',
    };
  }

  function getCheckoutRedirectUrl(invoiceUrl) {
    const primaryDomain = window.wn?.datastore?.shop?.primary_domain || window.location.hostname;
    return invoiceUrl.replace('checkout.shopify.com', primaryDomain);
  }

  function applyLineItemPrices(drawer, cart, discount, moneyFormat) {
    cart.items.forEach((item) => {
      const priceContainer = drawer.querySelector(`.wn-price-item_${item.variant_id}`);
      if (!priceContainer) return;

      const unitDiscounted = Math.max(Math.round(item.price * (1 - discount.percent)), 0);

      priceContainer.innerHTML = [
        `<span class="wn-line-item-original-price">${formatMoney(item.price, moneyFormat)}</span>`,
        `<span class="wn-line-item-discounted-price">${formatMoney(unitDiscounted, moneyFormat)}</span>`,
      ].join('');
    });
  }

  function applySubtotal(drawer, discount, moneyFormat) {
    const subtotalEl = drawer.querySelector('.cart-drawer__subtotal-price');
    if (!subtotalEl) return;

    subtotalEl.innerHTML = [
      `<span class="wn-original-subtotal">${formatMoney(discount.originalTotal, moneyFormat)}</span>`,
      `<span class="wn-discounted-subtotal">${formatMoney(discount.totalPrice, moneyFormat)}</span>`,
    ].join('');
  }

  async function applyWholesaleCartDrawerPrices() {
    const drawer = getDrawer();
    if (!drawer || !isWholesaleCustomer()) return;

    const groups = await getDiscountGroups();
    if (!groups.length) return;

    let cart;
    try {
      cart = await fetchCart();
    } catch (error) {
      console.warn('Wholesale cart drawer: unable to load cart', error);
      return;
    }

    if (!cart.items?.length) return;

    const discount = calculateBestDiscount(cart, groups, getCustomerTags());
    if (!discount) return;

    const moneyFormat = window.wn?.datastore?.shop?.money_format || '${{amount}}';
    applyLineItemPrices(drawer, cart, discount, moneyFormat);
    applySubtotal(drawer, discount, moneyFormat);
  }

  function setCheckoutLoading(button, isLoading) {
    if (!button) return;

    if (isLoading) {
      button.setAttribute('aria-disabled', 'true');
      button.classList.add('is-loading');
      button.dataset.originalText = button.textContent.trim();
      button.textContent = 'Processing...';
      return;
    }

    button.removeAttribute('aria-disabled');
    button.classList.remove('is-loading');
    if (button.dataset.originalText) {
      button.textContent = button.dataset.originalText;
      delete button.dataset.originalText;
    }
  }

  function removeRedoFromDrawer() {
    // Only strip Redo for wholesale customers; retail keeps Checkout+ in the drawer.
    if (!isWholesaleCustomer()) return;
    const drawer = getDrawer();
    if (!drawer) return;

    drawer.querySelectorAll('redo-shopify-toggle').forEach((element) => element.remove());
  }

  function watchDrawerForRedo() {
    const drawer = getDrawer();
    if (!drawer || drawer.dataset.redoWatcher === 'true') return;

    drawer.dataset.redoWatcher = 'true';

    const observer = new MutationObserver(() => {
      removeRedoFromDrawer();
    });

    observer.observe(drawer, { childList: true, subtree: true });
    removeRedoFromDrawer();
  }

  async function runWholesaleCheckout(button) {
    const groups = await getDiscountGroups();
    if (!groups.length) {
      window.location.href = button.href;
      return;
    }

    let cart;
    try {
      cart = await fetchCart();
    } catch (error) {
      console.warn('Wholesale cart drawer: unable to load cart for checkout', error);
      window.location.href = button.href;
      return;
    }

    const discount = calculateBestDiscount(cart, groups, getCustomerTags());
    if (!discount) {
      window.location.href = button.href;
      return;
    }

    const shop = getShopDomain();
    if (!shop) {
      window.location.href = button.href;
      return;
    }

    setCheckoutLoading(button, true);

    try {
      const response = await fetch(`${CHECKOUT_URL}?shopify_domain=${encodeURIComponent(shop)}`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json; charset=utf-8',
        },
        body: JSON.stringify(buildDraftOrderPayload(cart, discount)),
      });

      const data = await response.json();

      if (!response.ok || !data.invoice_url) {
        throw new Error(data.message || 'No checkout URL returned');
      }

      window.location.href = getCheckoutRedirectUrl(data.invoice_url);
    } catch (error) {
      console.error('Wholesale cart drawer: checkout failed', error);
      setCheckoutLoading(button, false);
      window.location.href = button.href;
    }
  }

  async function handleWholesaleCheckout(event) {
    const button = event.target.closest('[data-wholesale-checkout], .js-wholesale-checkout');
    if (!button || !button.closest('.js-cart-drawer, cart-drawer')) return;
    if (!isWholesaleCustomer()) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    await runWholesaleCheckout(button);
  }

  async function handleDrawerCheckoutClick(event) {
    if (!isWholesaleCustomer()) return;

    const drawer = getDrawer();
    if (!drawer) return;

    const path = event.composedPath();
    const isInDrawer = path.some((node) => node === drawer);
    if (!isInDrawer) return;

    const clickedRedo = path.find(
      (node) =>
        node instanceof Element &&
        (node.matches('[data-target="coverage-button"]') || node.matches('[data-target="non-coverage-button"]'))
    );
    if (!clickedRedo) return;

    const redoHost = path.find((node) => node instanceof Element && node.tagName === 'REDO-SHOPIFY-TOGGLE');
    if (!redoHost || !drawer.contains(redoHost)) return;

    const wholesaleButton = drawer.querySelector('[data-wholesale-checkout], .js-wholesale-checkout');
    if (!wholesaleButton) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    await runWholesaleCheckout(wholesaleButton);
  }

  function scheduleApply() {
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(() => {
      applyWholesaleCartDrawerPrices();
      removeRedoFromDrawer();
    }, 150);
  }

  function handleCartDrawerEvent(event) {
    const drawer = getDrawer();
    if (!drawer) return;

    if (event.type === 'on:cart:after-merge' && !event.target.closest('.js-cart-drawer, cart-drawer')) {
      return;
    }

    scheduleApply();
    watchDrawerForRedo();
  }

  document.addEventListener('click', handleDrawerCheckoutClick, true);
  document.addEventListener('click', handleWholesaleCheckout, true);
  document.addEventListener('on:cart:after-merge', handleCartDrawerEvent);
  document.addEventListener('on:cart-drawer:after-open', handleCartDrawerEvent);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      scheduleApply();
      watchDrawerForRedo();
    });
  } else {
    scheduleApply();
    watchDrawerForRedo();
  }
})();
